import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { useExerciseSync } from '../context/ExerciseSyncContext';
 

// eslint-disable-next-line no-unused-vars
const HANDLE_SIZE = 6;
// eslint-disable-next-line no-unused-vars
const HANDLES = [
  { x: '0%', y: '0%', cursor: 'nwse-resize', name: 'top-left' },
  { x: '100%', y: '0%', cursor: 'nesw-resize', name: 'top-right' },
  { x: '100%', y: '100%', cursor: 'nwse-resize', name: 'bottom-right' },
  { x: '0%', y: '100%', cursor: 'nesw-resize', name: 'bottom-left' },
];

// Cache key for the "image_size_*" localStorage restore below — MUST derive
// keys identically to the matching hashImageSrc() in TextEditor.js (the
// NodeView's own update() reads the same keys this saves), or a size saved
// here is never found there. Hashes the FULL src rather than truncating to
// a short prefix — every JPEG data URL this app produces via
// canvas.toDataURL() starts with near-identical header bytes (same
// encoder, same fixed quality), so a short-prefix key routinely collided
// between totally unrelated images, applying one image's cached size to
// another's node.
const hashImageSrc = (src) => {
  const s = String(src || '');
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
};

 

const ImageNodeView = ({ node, updateAttributes, selected, editor, getPos, deleteNode, isStudent = false }) => {
  // Live drawing strokes ride the same direct per-object realtime channel
  // used by exercises (ExerciseSyncContext), NOT the whole-document
  // editorContents/setContent() channel every other attrs change uses.
  // That channel filters out a peer's own echoes at the SOURCE
  // (subscribeExerciseStates ignores same-role broadcasts) and never
  // triggers a document-level reconciliation, so it's structurally immune
  // to the "teacher's own drawing round-trips back through the student's
  // echo and clobbers the teacher's own in-progress second drawing
  // session" failure mode that kept resurfacing despite fixing the
  // document-sync side of this repeatedly. Only the FINAL flattened image
  // (on draw-mode exit) still goes through updateAttributes/the document
  // channel — a one-time settle, which that channel already handles fine
  // for text; it's the high-frequency LIVE stream that was fighting it.
  const { otherState: liveOtherState, updateState: updateLiveState } = useExerciseSync(node.attrs.imageId);
  // The peer's in-progress drawing, shown as a src override while they're
  // actively drawing. Cleared once their session ends (saveDrawingToImage
  // broadcasts liveSrc: null) or after 30s with no new broadcast — guards
  // against a stale overlay lingering forever if a peer's browser closes
  // mid-stroke without a clean exit.
  const [peerLiveSrc, setPeerLiveSrc] = useState(null);
  useEffect(() => {
    const check = () => {
      const liveAt = liveOtherState.liveAt;
      const isFresh = typeof liveAt === 'number' && (Date.now() - liveAt) < 30000;
      setPeerLiveSrc(isFresh ? (liveOtherState.liveSrc || null) : null);
    };
    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [liveOtherState.liveSrc, liveOtherState.liveAt]);

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  // Tracks whether the user actually drew/erased a stroke in the current
  // drawing session. Used instead of scanning the canvas's alpha channel
  // for "anything opaque" — once the canvas is seeded with the current
  // image (see the seedImg effect below, which lets the eraser reach
  // previously-baked-in strokes), the whole canvas is opaque from the
  // start, so an alpha scan would always report "has drawing" even when
  // nothing changed.
  const hasStrokeRef = useRef(false);
  // Whether the working canvas has actually been seeded with the current
  // image yet (the seeding load is async — see the canvas-init effect
  // below). flattenCanvasToDataUrl refuses to run before this is true, so
  // a fast flush/save can never export a still-blank canvas.
  const canvasSeededRef = useRef(false);
  const drawingPanelRef = useRef(null);
  const lastResizeRef = useRef({ width: null, height: null });
  const aspectRatioRef = useRef(null); // Сохраняем aspect ratio изображения
  const imgRef = useRef(null); // Ref для прямого доступа к img элементу
  const originalSrcRef = useRef(node.attrs.src); // Сохраняем исходный src (не обновляется при сохранении рисунков)
  // Сохраняем самое первое исходное изображение (без всех рисунков) для кнопки Clear
  // Инициализируем только если это не dataUrl (изображение с рисунками)
  const firstOriginalImageSrcRef = useRef(
    node.attrs.src && !node.attrs.src.startsWith('data:image/') ? node.attrs.src : null
  );
  // Сохраняем размеры изображения для масштабирования текста
  const imageSizeRef = useRef({ width: null, height: null });
  const [dragging, setDragging] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingColor, setDrawingColor] = useState('#ff0000');
  const [brushSize, setBrushSize] = useState(5);
  const [isEraser, setIsEraser] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0, visible: false });
  const [isTextMode, setIsTextMode] = useState(false);
  const [textElements, setTextElements] = useState(() => {
    if (!node.attrs.textElements) return [];
    try { return JSON.parse(node.attrs.textElements); } catch { return []; }
  });
  const [editingTextId, setEditingTextId] = useState(null);
  const isFirstTextRenderRef = useRef(true);
  const lastLocalSerializedRef = useRef(node.attrs.textElements ?? null);
  const [stableSrc, setStableSrc] = useState(node.attrs.src || '');
  const [draggingTextId, setDraggingTextId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const newlyCreatedTextIdRef = useRef(null);
  const textInputRef = useRef(null);
  
  // Вспомогательная функция для немедленного обновления стилей input поля
  const updateInputStyles = useCallback((updates, elementId) => {
    if (textInputRef.current && editingTextId === elementId) {
      const input = textInputRef.current;
      if (updates.color !== undefined) {
        input.style.color = updates.color;
      }
      if (updates.fontSize !== undefined) {
        input.style.fontSize = `${updates.fontSize}px`;
      }
      if (updates.fontFamily !== undefined) {
        input.style.fontFamily = updates.fontFamily;
      }
      if (updates.fontWeight !== undefined) {
        input.style.fontWeight = updates.fontWeight;
      }
      if (updates.fontStyle !== undefined) {
        input.style.fontStyle = updates.fontStyle;
      }
      if (updates.textDecoration !== undefined) {
        input.style.textDecoration = updates.textDecoration;
      }
    }
  }, [editingTextId]);
  
  // Обновляем стили input поля при изменении textElements
  useEffect(() => {
    if (editingTextId && textInputRef.current) {
      const textEl = textElements.find(t => t.id === editingTextId);
      if (textEl) {
        const input = textInputRef.current;
        input.style.fontSize = `${textEl.fontSize}px`;
        input.style.color = textEl.color;
        input.style.fontFamily = textEl.fontFamily;
        input.style.fontWeight = textEl.fontWeight || 'normal';
        input.style.fontStyle = textEl.fontStyle || 'normal';
        input.style.textDecoration = textEl.textDecoration || 'none';
      }
    }
  }, [textElements, editingTextId]);

  // Refs to avoid recreating canvas on setting changes
  const drawingColorRef = useRef(drawingColor);
  const brushSizeRef = useRef(brushSize);
  const isEraserRef = useRef(isEraser);
  useEffect(() => { drawingColorRef.current = drawingColor; }, [drawingColor]);
  useEffect(() => { brushSizeRef.current = brushSize; }, [brushSize]);
  useEffect(() => { isEraserRef.current = isEraser; }, [isEraser]);

  // Сохраняем размеры в localStorage
  const saveImageSize = (width, height) => {
    const key = `image_size_${hashImageSrc(node.attrs.src)}`;
    localStorage.setItem(key, JSON.stringify({ width, height }));
  };

  // Загружаем размеры из localStorage
  const loadImageSize = () => {
    const key = `image_size_${hashImageSrc(node.attrs.src)}`;
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  };

  // Сохраняем исходный src при монтировании и обновляем только при реальном изменении
  useEffect(() => {
    if (node.attrs.src && node.attrs.src !== originalSrcRef.current) {
      // Проверяем, является ли новый src dataUrl (изображение с рисунками)
      const isDataUrl = node.attrs.src.startsWith('data:image/');
      const wasDataUrl = originalSrcRef.current && originalSrcRef.current.startsWith('data:image/');
      
      // Если это переход с обычного URL на dataUrl, это сохранение рисунка
      // В этом случае НЕ обновляем originalSrcRef, чтобы он сохранил исходное изображение
      if (!wasDataUrl && isDataUrl) {
        // Это первое сохранение рисунка - сохраняем исходное изображение в firstOriginalImageSrcRef
        // Убеждаемся, что firstOriginalImageSrcRef содержит исходное изображение (не dataUrl)
        // НЕ перезаписываем firstOriginalImageSrcRef, если он уже установлен и не является dataUrl
        if (!firstOriginalImageSrcRef.current || firstOriginalImageSrcRef.current.startsWith('data:image/')) {
          // Используем originalSrcRef, который должен содержать исходное изображение
          if (originalSrcRef.current && !originalSrcRef.current.startsWith('data:image/')) {
            // Если это blob URL, конвертируем его в dataUrl, чтобы он не стал недействительным
            if (originalSrcRef.current.startsWith('blob:')) {
              // Конвертируем blob URL в dataUrl
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const dataUrl = canvas.toDataURL('image/png');
                firstOriginalImageSrcRef.current = dataUrl;
                console.log('Converted blob URL to dataUrl and saved to firstOriginalImageSrcRef');
              };
              img.onerror = () => {
                // Если не удалось конвертировать, сохраняем как есть
                firstOriginalImageSrcRef.current = originalSrcRef.current;
                console.warn('Failed to convert blob URL to dataUrl, saving as is');
              };
              img.src = originalSrcRef.current;
            } else {
              // Если это не blob URL, сохраняем как есть
              firstOriginalImageSrcRef.current = originalSrcRef.current;
              console.log('Saved original image to firstOriginalImageSrcRef:', originalSrcRef.current.substring(0, 50) + '...');
            }
          } else {
            console.warn('originalSrcRef contains dataUrl, cannot save original image');
          }
        }
        // НЕ обновляем originalSrcRef, чтобы он сохранил исходное изображение
        setStableSrc(node.attrs.src);
      } else if (wasDataUrl && isDataUrl) {
        // Это последующее сохранение рисунка - не обновляем originalSrcRef и firstOriginalImageSrcRef
        setStableSrc(node.attrs.src);
      } else if (!isDataUrl && !wasDataUrl) {
        // Это замена изображения на другое - обновляем все refs
        firstOriginalImageSrcRef.current = node.attrs.src;
        originalSrcRef.current = node.attrs.src;
        setStableSrc(node.attrs.src);
      } else {
        // В остальных случаях обновляем stableSrc
        setStableSrc(node.attrs.src);
      }
    } else if (!node.attrs.src && originalSrcRef.current && !stableSrc) {
      // Если src потерялся, используем сохраненный
      setStableSrc(originalSrcRef.current);
      // Обновляем firstOriginalImageSrcRef только если он еще не установлен
      if (!firstOriginalImageSrcRef.current) {
        firstOriginalImageSrcRef.current = originalSrcRef.current;
      }
    }
  }, [node.attrs.src, stableSrc]);

  // Восстанавливаем сохраненный размер, если атрибуты отсутствуют/auto
  useEffect(() => {
    const saved = loadImageSize();
    if (saved && (!node.attrs.width || node.attrs.width === 'auto' || !node.attrs.height || node.attrs.height === 'auto')) {
      safeUpdateAttributes({ width: saved.width, height: saved.height });
    } else if (saved && node.attrs.width && node.attrs.height) {
      // Если размеры есть в атрибутах, обновляем localStorage для синхронизации
      saveImageSize(node.attrs.width, node.attrs.height);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Синхронизируем размеры из localStorage при изменении атрибутов
  useEffect(() => {
    if (node.attrs.width && node.attrs.height && 
        node.attrs.width !== 'auto' && node.attrs.height !== 'auto') {
      // Сохраняем размеры в localStorage при изменении атрибутов
      saveImageSize(node.attrs.width, node.attrs.height);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.attrs.width, node.attrs.height]);
  
  // Масштабируем текстовые элементы при изменении размера изображения
  useEffect(() => {
    if (!imgRef.current || textElements.length === 0) return;
    
    const img = imgRef.current;
    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    
    const currentWidth = rect.width;
    const currentHeight = rect.height;
    const oldWidth = imageSizeRef.current.width;
    const oldHeight = imageSizeRef.current.height;
    
    // Если размеры изменились, масштабируем текст
    if (oldWidth && oldHeight && (oldWidth !== currentWidth || oldHeight !== currentHeight)) {
      const scaleX = currentWidth / oldWidth;
      const scaleY = currentHeight / oldHeight;
      // Используем средний масштаб для сохранения пропорций
      const scale = (scaleX + scaleY) / 2;
      
      setTextElements(prev => prev.map(textEl => {
        // Если есть проценты, используем их для пересчета позиции
        if (textEl.xPercent !== undefined && textEl.yPercent !== undefined) {
          return {
            ...textEl,
            x: (textEl.xPercent / 100) * currentWidth,
            y: (textEl.yPercent / 100) * currentHeight,
            fontSize: textEl.baseFontSize ? textEl.baseFontSize * scale : textEl.fontSize * scale,
            baseImageWidth: currentWidth,
            baseImageHeight: currentHeight
          };
        } else {
          // Если процентов нет, пересчитываем из старых координат
          return {
            ...textEl,
            x: textEl.x * scaleX,
            y: textEl.y * scaleY,
            xPercent: (textEl.x / currentWidth) * 100,
            yPercent: (textEl.y / currentHeight) * 100,
            fontSize: textEl.baseFontSize ? textEl.baseFontSize * scale : textEl.fontSize * scale,
            baseFontSize: textEl.baseFontSize || textEl.fontSize,
            baseImageWidth: currentWidth,
            baseImageHeight: currentHeight
          };
        }
      }));
      
      // Обновляем сохраненные размеры
      imageSizeRef.current = { width: currentWidth, height: currentHeight };
    } else if (!oldWidth || !oldHeight) {
      // Если размеры еще не сохранены, сохраняем их
      imageSizeRef.current = { width: currentWidth, height: currentHeight };
    }
  }, [node.attrs.width, node.attrs.height, textElements.length]);

  // Выравнивание изображения больше не поддерживается







  // Безопасный updateAttributes
  const safeUpdateAttributes = useCallback((attrs) => {
    if (typeof updateAttributes === 'function') {
      updateAttributes(attrs);
    } else if (editor && editor.commands && typeof editor.commands.updateAttributes === 'function') {
      // eslint-disable-next-line no-console
      console.warn('ImageNodeView: updateAttributes is not a function, using editor.commands.updateAttributes');
      editor.commands.updateAttributes('image', attrs);
    } else {
      // eslint-disable-next-line no-console
      console.error('ImageNodeView: updateAttributes is not a function and no fallback available');
    }
  }, [updateAttributes, editor]);

  // useRef для safeUpdateAttributes
  const safeUpdateAttributesRef = useRef(safeUpdateAttributes);
  useEffect(() => {
    safeUpdateAttributesRef.current = safeUpdateAttributes;
  }, [safeUpdateAttributes]);

  // Persist text elements to node attribute so they survive reload.
  // Guard against a ping-pong loop seen on OLDER lessons: if the JSON
  // string stored in the DB doesn't byte-for-byte match what
  // JSON.stringify produces on re-serialization (key order, whitespace,
  // or escaping drift from an earlier save format), this effect and the
  // "sync from node.attrs" effect below can keep re-triggering each
  // other — every parent re-render (e.g. every keystroke elsewhere in
  // the same container) re-reads node.attrs, sees a "different" string,
  // updates local state, which re-fires THIS effect, which writes back a
  // "different" string again, forever. Comparing against the current
  // node.attrs.textElements directly (the real source of truth) instead
  // of only the local ref breaks that loop: we only write when the
  // content has actually, semantically changed.
  useEffect(() => {
    if (isFirstTextRenderRef.current) {
      isFirstTextRenderRef.current = false;
      return;
    }
    const json = textElements.length > 0 ? JSON.stringify(textElements) : null;
    if (json === (node.attrs.textElements ?? null)) {
      lastLocalSerializedRef.current = json;
      return;
    }
    lastLocalSerializedRef.current = json;
    safeUpdateAttributesRef.current({ textElements: json });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textElements]);

  // Sync textElements state when node.attrs.textElements is updated externally
  // (e.g. teacher annotations arriving on the student's side via realtime broadcast)
  useEffect(() => {
    const incoming = node.attrs.textElements ?? null;
    if (incoming === lastLocalSerializedRef.current) return;
    if (editingTextId !== null || draggingTextId !== null) return;
    try {
      const parsed = incoming ? JSON.parse(incoming) : [];
      lastLocalSerializedRef.current = incoming;
      setTextElements(parsed);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.attrs.textElements, editingTextId, draggingTextId]);

  // Панель выравнивания удалена

  // Удаление текстового элемента, если пользователь кликнул на изображении, но не начал вводить
  useEffect(() => {
    if (!isTextMode || !newlyCreatedTextIdRef.current) return;
    
    const handleImageClick = (e) => {
      // Проверяем, не кликнули ли на текстовый элемент
      const clickedOnTextElement = textElements.some(textEl => {
        const textElDiv = document.querySelector(`[data-text-id="${textEl.id}"]`);
        if (textElDiv) {
          const rect = textElDiv.getBoundingClientRect();
          return e.clientX >= rect.left && e.clientX <= rect.right &&
                 e.clientY >= rect.top && e.clientY <= rect.bottom;
        }
        return false;
      });
      
      // Если кликнули на изображении (не на текстовом элементе) и есть новый элемент без изменений
      if (!clickedOnTextElement && newlyCreatedTextIdRef.current) {
        const newTextEl = textElements.find(t => t.id === newlyCreatedTextIdRef.current);
        if (newTextEl && !newTextEl.text.trim()) {
          setTextElements(prev => prev.filter(t => t.id !== newlyCreatedTextIdRef.current));
          setEditingTextId(null);
          newlyCreatedTextIdRef.current = null;
        }
      }
    };
    
    const img = imgRef.current;
    if (img) {
      img.addEventListener('click', handleImageClick);
      return () => {
        img.removeEventListener('click', handleImageClick);
      };
    }
  }, [isTextMode, textElements]);
  
  // Удаление текстового элемента, если пользователь кликнул на изображении, но не начал вводить
  useEffect(() => {
    if (!isTextMode || !newlyCreatedTextIdRef.current) return;
    
    const handleContainerClick = (e) => {
      // Проверяем, не кликнули ли на текстовый элемент или input
      const target = e.target;
      const isTextElement = target.closest('[data-text-id]');
      const isInput = target.tagName === 'INPUT' && target.type === 'text';
      
      // Если кликнули на контейнере (не на текстовом элементе), проверяем новый элемент
      if (!isTextElement && !isInput && newlyCreatedTextIdRef.current) {
        const newTextEl = textElements.find(t => t.id === newlyCreatedTextIdRef.current);
        // Если элемент существует и текст не изменился, удаляем его
        if (newTextEl && !newTextEl.text.trim()) {
          setTextElements(prev => prev.filter(t => t.id !== newlyCreatedTextIdRef.current));
          setEditingTextId(null);
          newlyCreatedTextIdRef.current = null;
        }
      }
    };
    
    const container = containerRef.current;
    if (container) {
      // Используем capture phase, чтобы обработать до других обработчиков
      container.addEventListener('mousedown', handleContainerClick, true);
      return () => {
        container.removeEventListener('mousedown', handleContainerClick, true);
      };
    }
  }, [isTextMode, textElements]);
  
  // Обработка перетаскивания текста
  useEffect(() => {
    if (!draggingTextId) return;
    
    const handleMouseMove = (e) => {
      const imgRect = imgRef.current?.getBoundingClientRect();
      if (imgRect) {
        const newX = e.clientX - imgRect.left - dragOffset.x;
        const newY = e.clientY - imgRect.top - dragOffset.y;
        
        // Ограничиваем перемещение границами изображения
        const imgWidth = imgRect.width;
        const imgHeight = imgRect.height;
        const constrainedX = Math.max(0, Math.min(newX, imgWidth - 50)); // 50 - примерная ширина текста
        const constrainedY = Math.max(0, Math.min(newY, imgHeight - 30)); // 30 - примерная высота текста
        
        // Обновляем координаты и проценты при перетаскивании
        setTextElements(prev => prev.map(t => {
          if (t.id === draggingTextId) {
            return {
              ...t,
              x: constrainedX,
              y: constrainedY,
              xPercent: (constrainedX / imgWidth) * 100,
              yPercent: (constrainedY / imgHeight) * 100
            };
          }
          return t;
        }));
      }
    };
    
    const handleMouseUp = () => {
      setDraggingTextId(null);
      setDragOffset({ x: 0, y: 0 });
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingTextId, dragOffset]);

  // Сохраняем исходное изображение перед началом рисования
  const originalImageSrcRef = useRef(null);
  // Флаг для отслеживания процесса сохранения
  const isSavingRef = useRef(false);
  // Флаг для предотвращения повторных вызовов сохранения
  const savingInProgressRef = useRef(false);
  
  // Composite the working canvas into a full-resolution data URL. The
  // canvas is seeded with the current image the moment draw mode is
  // entered (see the seedImg effect below), so it ALREADY contains the
  // photo plus any prior strokes plus whatever the user just drew/erased —
  // no need to separately reload the base image via a second `Image()`
  // round trip like the old two-step composite did.
  const flattenCanvasToDataUrl = useCallback(() => {
    const img = imgRef.current;
    const drawingCanvas = canvasRef.current;
    if (!img || !drawingCanvas) return null;
    // Refuse to export before the canvas has actually been seeded with the
    // photo (see canvasSeededRef) — flattening a still-transparent canvas
    // exports as solid black (JPEG has no alpha channel), replacing the
    // photo. The caller decides whether to skip this attempt (flush) or
    // wait and retry (final save).
    if (!canvasSeededRef.current) return null;
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = img.naturalWidth || img.offsetWidth;
    finalCanvas.height = img.naturalHeight || img.offsetHeight;
    const finalCtx = finalCanvas.getContext('2d');
    finalCtx.drawImage(drawingCanvas, 0, 0, drawingCanvas.width, drawingCanvas.height, 0, 0, finalCanvas.width, finalCanvas.height);
    return finalCanvas.toDataURL('image/jpeg', 0.7);
  }, []);

  // Periodic broadcast while actively drawing, WITHOUT exiting draw mode or
  // clearing the canvas — this is what makes strokes visible to the other
  // side (teacher/student) in near-real-time instead of only once the pencil
  // tool is toggled off. Rides the dedicated per-image exercise-sync
  // channel (updateLiveState), NOT node attrs/the document channel — see
  // the comment on useExerciseSync above for why. The local canvas keeps
  // rendering the user's live in-progress strokes untouched.
  const flushLiveDrawing = useCallback(() => {
    if (!hasStrokeRef.current || savingInProgressRef.current) return;
    const dataUrl = flattenCanvasToDataUrl();
    if (!dataUrl) return;
    originalImageSrcRef.current = dataUrl;
    updateLiveState({ liveSrc: dataUrl, liveAt: Date.now() });
  }, [flattenCanvasToDataUrl, updateLiveState]);
  // Kept in a ref (same pattern as drawingColorRef etc.) so the canvas-init
  // effect below doesn't need flushLiveDrawing in its dependency array —
  // that effect only re-runs on [isDrawingMode], and adding a
  // useCallback-identity dependency would risk tearing down/recreating the
  // live canvas (losing in-progress strokes) whenever an unrelated render
  // changes that callback's identity.
  const flushLiveDrawingRef = useRef(flushLiveDrawing);
  useEffect(() => { flushLiveDrawingRef.current = flushLiveDrawing; }, [flushLiveDrawing]);

  // Финальное сохранение рисунка на изображение — flattens, clears the
  // canvas, and exits drawing mode.
  const saveDrawingToImage = useCallback((retriesLeft = 40) => {
    if (!canvasRef.current || !imgRef.current) {
      // Если нет canvas или изображения, просто выходим из режима
      setIsDrawingMode(false);
      return;
    }

    // Предотвращаем повторные вызовы
    if (savingInProgressRef.current) {
      return;
    }

    // Сохраняем, только если пользователь реально провёл штрих (см.
    // hasStrokeRef — alpha-скан больше не годится, т.к. canvas теперь
    // всегда непрозрачен с самого начала сессии рисования).
    if (!hasStrokeRef.current) {
      setIsDrawingMode(false);
      return;
    }

    // The seeding load (see the canvas-init effect) is normally near-
    // instant, but if the user draws and immediately exits before it
    // resolves, wait for it rather than either losing the stroke or (the
    // bug this replaced) exporting a still-blank canvas as solid black.
    // 40 retries * 50ms = 2s ceiling; if it genuinely never resolves by
    // then, fall through and let flattenCanvasToDataUrl's own guard skip
    // the export (same as today — the drawing is lost, but nothing goes
    // black).
    if (!canvasSeededRef.current && retriesLeft > 0) {
      setTimeout(() => saveDrawingToImage(retriesLeft - 1), 50);
      return;
    }

    // Устанавливаем флаги сохранения
    isSavingRef.current = true;
    savingInProgressRef.current = true;

    try {
      const dataUrl = flattenCanvasToDataUrl();
      if (!dataUrl) {
        isSavingRef.current = false;
        savingInProgressRef.current = false;
        setIsDrawingMode(false);
        return;
      }

      // Обновляем сохраненное исходное изображение для следующего сеанса рисования
      originalImageSrcRef.current = dataUrl;

      // Обновляем stableSrc напрямую СНАЧАЛА, чтобы изображение обновилось сразу
      setStableSrc(dataUrl);

      // Обновляем атрибуты узла
      safeUpdateAttributes({ src: dataUrl });

      // Tell the peer the live stream ended — otherwise a stale
      // peerLiveSrc overlay (see useExerciseSync above) could keep
      // showing their old in-progress frame instead of falling back to
      // this now-finalized node.attrs.src once it arrives via the
      // document channel.
      updateLiveState({ liveSrc: null });

      // Принудительно обновляем src изображения, чтобы оно обновилось визуально
      if (imgRef.current) {
        imgRef.current.src = dataUrl;
        const img = imgRef.current;
        const updateImg = () => {
          if (img && img.src !== dataUrl) {
            img.src = dataUrl;
          }
        };
        img.onload = updateImg;
        setTimeout(updateImg, 10);
      }

      // Очищаем canvas для рисования только после успешного сохранения
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }

      // Сбрасываем флаги сохранения
      isSavingRef.current = false;
      savingInProgressRef.current = false;

      // Выходим из режима рисования только после успешного сохранения
      // Небольшая задержка, чтобы изображение успело обновиться
      setTimeout(() => {
        setIsDrawingMode(false);
      }, 200);
    } catch (error) {
      console.error('Error saving drawing:', error);
      isSavingRef.current = false;
      savingInProgressRef.current = false;
      // В случае ошибки все равно выходим из режима
      setIsDrawingMode(false);
    }
  }, [flattenCanvasToDataUrl, safeUpdateAttributes, updateLiveState]);

  // Ref для отслеживания режима рисования в обработчиках
  const isDrawingModeRef = useRef(isDrawingMode);
  useEffect(() => {
    isDrawingModeRef.current = isDrawingMode;
  }, [isDrawingMode]);

  // Во время рисования: клик в редакторе вне изображения/панели — сохранить и выйти из режима
  useEffect(() => {
    if (!isDrawingMode) {
      // НЕ сбрасываем сохраненное изображение при выходе из режима
      // Оно нужно для следующего сеанса рисования
      return;
    }
    
    // Сохраняем исходное изображение при входе в режим рисования
    if (imgRef.current) {
      // НЕ обновляем firstOriginalImageSrcRef при входе в режим рисования
      // Он должен содержать самое первое исходное изображение без рисунков
      // Обновляем originalImageSrcRef на текущее изображение (которое может содержать уже сохраненные рисунки)
      // Это нужно для того, чтобы при сохранении нового рисунка использовалось изображение с предыдущими рисунками
      originalImageSrcRef.current = imgRef.current.src;
    }
    
    const handleGlobalMouseDown = (e) => {
      // Проверяем актуальное значение через ref
      if (!isDrawingModeRef.current) return;
      
      const target = e.target;
      const inEditor = !!(target && typeof target.closest === 'function' && target.closest('.ProseMirror'));
      const inImage = !!(containerRef.current && containerRef.current.contains(target));
      const inPanel = !!(drawingPanelRef.current && drawingPanelRef.current.contains && drawingPanelRef.current.contains(target));
      
      if (inEditor && !inImage && !inPanel) {
        // Сохраняем только если пользователь реально провёл штрих
        if (canvasRef.current && hasStrokeRef.current) {
          // saveDrawingToImage сам вызовет setIsDrawingMode(false) после сохранения
          saveDrawingToImage();
        } else {
          // Если нет рисунка, просто выходим из режима без сохранения
          setIsDrawingMode(false);
        }
      }
    };
    
    // Также обрабатываем начало ввода текста (фокус на редакторе)
    const handleEditorFocus = (e) => {
      // Проверяем актуальное значение через ref
      if (!isDrawingModeRef.current) return;
      
      const target = e.target;
      const inImage = !!(containerRef.current && containerRef.current.contains(target));
      const inPanel = !!(drawingPanelRef.current && drawingPanelRef.current.contains && drawingPanelRef.current.contains(target));
      
      // Если фокус не в изображении и не в панели, и есть что сохранить
      if (!inImage && !inPanel) {
        // Сохраняем рисунок сразу при фокусе на редакторе, только если
        // пользователь реально провёл штрих
        if (canvasRef.current && !savingInProgressRef.current) {
          if (hasStrokeRef.current) {
            // Сохраняем сразу, без задержки
            saveDrawingToImage();
          } else {
            // Если нет рисунка, просто выходим из режима без сохранения
            setIsDrawingMode(false);
          }
        }
      }
    };
    
    // Обрабатываем начало печати (keydown) - сохраняем рисунок сразу
    const handleKeyDown = (e) => {
      // Проверяем актуальное значение через ref
      if (!isDrawingModeRef.current) return;
      
      // Проверяем, что это обычная печатная клавиша (не служебные)
      const isPrintableKey = e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter';
      
      if (isPrintableKey) {
        const target = e.target;
        const inImage = !!(containerRef.current && containerRef.current.contains(target));
        const inPanel = !!(drawingPanelRef.current && drawingPanelRef.current.contains && drawingPanelRef.current.contains(target));
        const inEditor = !!(target && typeof target.closest === 'function' && target.closest('.ProseMirror'));
        
        // Если печатаем в редакторе, но не в изображении и не в панели
        if (inEditor && !inImage && !inPanel) {
          // Сохраняем рисунок сразу при начале печати, только если
          // пользователь реально провёл штрих
          if (canvasRef.current && !savingInProgressRef.current) {
            if (hasStrokeRef.current) {
              // Сохраняем сразу, без задержки
              saveDrawingToImage();
            } else {
              // Если нет рисунка, просто выходим из режима без сохранения
              setIsDrawingMode(false);
            }
          }
        }
      }
    };
    
    document.addEventListener('mousedown', handleGlobalMouseDown);
    document.addEventListener('focusin', handleEditorFocus);
    document.addEventListener('keydown', handleKeyDown, true); // Используем capture phase
    
    return () => {
      document.removeEventListener('mousedown', handleGlobalMouseDown);
      document.removeEventListener('focusin', handleEditorFocus);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isDrawingMode, saveDrawingToImage]);
  
  // Инициализация canvas для рисования
  useEffect(() => {
    if (!isDrawingMode || !containerRef.current) return;
    
    const img = containerRef.current.querySelector('img');
    if (!img) return;
    
    const canvas = document.createElement('canvas');
    canvas.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      cursor: crosshair;
      z-index: 5;
      pointer-events: auto;
    `;
    
    // Включаем pointer-events для canvas
    canvas.style.pointerEvents = 'auto';
    
    canvasRef.current = canvas;
    containerRef.current.appendChild(canvas);
    hasStrokeRef.current = false; // fresh session — nothing drawn yet

    const ctx = canvas.getContext('2d');
    let isDrawing = false;
    let lastX = 0;
    let lastY = 0;
    
    const resizeCanvas = () => {
      const rect = img.getBoundingClientRect();
      const newWidth = rect.width;
      const newHeight = rect.height;
      if (canvas.width === newWidth && canvas.height === newHeight) return;
      // Reassigning canvas.width/height always clears the ENTIRE pixel
      // buffer (HTML Canvas spec) — including the photo seedImg.onload
      // painted in and any strokes drawn since — but canvasSeededRef
      // doesn't know that. This function also runs on every window
      // 'resize' event (see handleResize below), so a resize firing AFTER
      // seeding (window resize, on-screen-keyboard dismiss, zoom, devtools
      // toggle) silently wiped the canvas back to fully transparent while
      // canvasSeededRef.current stayed true — the next flush/save then
      // flattened that transparent canvas and exported it as JPEG (no
      // alpha channel), turning the whole photo solid black. Snapshot
      // whatever's currently painted to an offscreen canvas first and
      // redraw it back afterward so a resize can no longer lose it.
      let snapshot = null;
      if (canvas.width > 0 && canvas.height > 0) {
        const temp = document.createElement('canvas');
        temp.width = canvas.width;
        temp.height = canvas.height;
        temp.getContext('2d').drawImage(canvas, 0, 0);
        snapshot = temp;
      }
      canvas.width = newWidth;
      canvas.height = newHeight;
      canvas.style.width = newWidth + 'px';
      canvas.style.height = newHeight + 'px';
      if (snapshot) {
        ctx.drawImage(snapshot, 0, 0, snapshot.width, snapshot.height, 0, 0, newWidth, newHeight);
      }
    };
    
    const startDrawing = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isDrawing = true;
      const rect = canvas.getBoundingClientRect();
      lastX = e.clientX - rect.left;
      lastY = e.clientY - rect.top;
    };
    
    const draw = (e) => {
      if (!isDrawing) return;

      e.preventDefault();
      e.stopPropagation();
      hasStrokeRef.current = true;

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Переключение в режим ластика (прорезаем прозрачностью)
      ctx.globalCompositeOperation = isEraserRef.current ? 'destination-out' : 'source-over';
      ctx.strokeStyle = drawingColorRef.current;
      ctx.lineWidth = brushSizeRef.current;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
      
      lastX = x;
      lastY = y;
    };
    
    const stopDrawing = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      isDrawing = false;
    };
    
    resizeCanvas();

    // Seed the canvas with the CURRENT image (which already includes any
    // strokes flattened from a previous drawing session) instead of leaving
    // it transparent. Without this, re-entering draw mode always starts
    // from a blank overlay — the eraser (destination-out) can only remove
    // pixels ON THAT OVERLAY, so old ink baked into the base image via
    // saveDrawingToImage was permanently un-erasable. Drawing the current
    // pixels onto the working canvas first means the eraser is now
    // genuinely erasing the same pixels the user sees.
    //
    // MUST go through a freshly-loaded Image with crossOrigin='anonymous'
    // rather than drawing the on-screen <img> directly — that <img> has no
    // crossOrigin set (setting one there risks the image failing to
    // display at all for sources without CORS headers), so drawImage()-ing
    // it into this canvas would permanently taint the canvas: every later
    // toDataURL() call in this session throws SecurityError, not just the
    // first one. canvasSeededRef below tracks whether this async load has
    // finished; flattenCanvasToDataUrl refuses to run until it has,
    // instead of flattening a still-transparent canvas — which, exported
    // as JPEG (no alpha channel), turned the whole photo solid black.
    canvasSeededRef.current = false;
    const seedImg = new Image();
    seedImg.crossOrigin = 'anonymous';
    seedImg.onload = () => {
      if (canvasRef.current === canvas) {
        ctx.drawImage(seedImg, 0, 0, canvas.width, canvas.height);
        canvasSeededRef.current = true;
      }
    };
    seedImg.src = img.src;

    canvas.addEventListener('mousedown', (e) => {
      // Проверяем, не кликнули ли мы на resize handle
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const width = rect.width;
      const height = rect.height;
      
      // Области resize handles (10px от краев)
      const isNearEdge = x < 10 || x > width - 10 || y < 10 || y > height - 10;
      
      if (isNearEdge) {
        e.preventDefault();
        e.stopPropagation();
        return; // Не начинаем рисование
      }
      
      startDrawing(e);
    });
    const handleHover = (e) => {
      const crect = containerRef.current.getBoundingClientRect();
      setCursorPos({
        x: e.clientX - crect.left,
        y: e.clientY - crect.top,
        visible: isEraserRef.current,
      });
    };

    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mousemove', handleHover);
    canvas.addEventListener('mouseenter', handleHover);
    canvas.addEventListener('mouseleave', () => setCursorPos((p) => ({ ...p, visible: false })));
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);
    
    // Поддержка touch событий
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousedown', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      startDrawing(mouseEvent);
    });
    
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      draw(mouseEvent);
    });
    
    canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      stopDrawing();
    });
    
    // Обработка изменения размера окна
    const handleResize = () => {
      resizeCanvas();
    };
    window.addEventListener('resize', handleResize);

    // Periodically broadcast the in-progress drawing so the other side
    // (teacher/student) sees strokes appear as they're made, not just once
    // the pencil tool is toggled off.
    const liveFlushInterval = setInterval(() => {
      flushLiveDrawingRef.current();
    }, 450);

    return () => {
      clearInterval(liveFlushInterval);
      // Не удаляем canvas, если идет процесс сохранения
      if (!isSavingRef.current) {
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
        }
      } else {
        // Если идет сохранение, удаляем canvas с небольшой задержкой
        setTimeout(() => {
          if (canvas && canvas.parentNode && !isSavingRef.current) {
            canvas.parentNode.removeChild(canvas);
          }
        }, 500);
      }
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mousemove', handleHover);
      canvas.removeEventListener('mouseenter', handleHover);
      canvas.removeEventListener('mouseleave', () => {});
      setCursorPos((p) => ({ ...p, visible: false }));
    };
  }, [isDrawingMode]);

  // Resize logic
  const handleResize = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const imgEl = containerRef.current.querySelector('img');
    
    if (!imgEl) return;
    
    // Получаем максимальную ширину контейнера редактора
    const editorContainer = containerRef.current.closest('.ProseMirror');
    // eslint-disable-next-line no-unused-vars
    const maxContainerWidth = editorContainer ? editorContainer.offsetWidth : window.innerWidth;
    
    // Получаем текущие размеры контейнера
    let startWidth = containerRef.current.offsetWidth;
    let startHeight = containerRef.current.offsetHeight;
    
    // Используем сохраненный aspect ratio или вычисляем из natural размеров
    let aspect;
    if (aspectRatioRef.current) {
      aspect = aspectRatioRef.current;
    } else {
      // Пытаемся получить из natural размеров
      const naturalWidth = imgEl.naturalWidth;
      const naturalHeight = imgEl.naturalHeight;
      if (naturalWidth && naturalHeight) {
        aspect = naturalWidth / naturalHeight;
        aspectRatioRef.current = aspect; // Сохраняем для будущего использования
      } else {
        // Fallback: используем текущие размеры
        aspect = startWidth / startHeight;
      }
    }

      const pos = getPos && getPos();
      let newWidth = node.attrs.width;
      let newHeight = node.attrs.height;
      if (node.attrs.width && !node.attrs.width.endsWith('px')) {
        newWidth = Math.round(imgEl.getBoundingClientRect().width) + 'px';
        startWidth = imgEl.getBoundingClientRect().width;
        // eslint-disable-next-line no-unused-vars
        const widthWasPercent = true;
      }
      if (node.attrs.height && !node.attrs.height.endsWith('px') && node.attrs.height !== 'auto') {
        newHeight = Math.round(imgEl.getBoundingClientRect().height) + 'px';
        startHeight = imgEl.getBoundingClientRect().height;
        // eslint-disable-next-line no-unused-vars
        const heightWasPercent = true;
      }
    
      if ((newWidth !== node.attrs.width || newHeight !== node.attrs.height) && typeof pos === 'number' && editor) {
        editor.chain().focus().command(({ tr }) => {
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            width: newWidth,
            height: newHeight,
          });
          setTimeout(() => {
            // eslint-disable-next-line no-unused-vars
            const newNode = editor.state.doc.nodeAt(pos);
            // no-op
          }, 0);
          return true;
        }).run();
      }

    let lastUpdate = 0;
    const onMove = (moveEvent) => {
      const now = Date.now();
      if (now - lastUpdate < 16) return; // Throttle to ~60fps
      lastUpdate = now;
      
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      
      // Всегда сохраняем пропорции изображения
      // Используем изменение по большей оси (dx или dy) для сохранения пропорций
        const widthChange = Math.abs(dx);
        const heightChange = Math.abs(dy);
        
      let newWidth, newHeight;
      
      // Определяем, по какой оси больше изменение
        if (widthChange > heightChange) {
          // Изменяем по ширине, высота пропорционально
        if (handle.name.includes('right')) {
          newWidth = startWidth + dx;
        } else if (handle.name.includes('left')) {
          newWidth = startWidth - dx;
        } else {
          newWidth = startWidth + dx;
        }
          newHeight = newWidth / aspect;
        } else {
          // Изменяем по высоте, ширина пропорционально
        if (handle.name.includes('bottom')) {
          newHeight = startHeight + dy;
        } else if (handle.name.includes('top')) {
          newHeight = startHeight - dy;
        } else {
          newHeight = startHeight + dy;
        }
          newWidth = newHeight * aspect;
        }
      
      // Ограничиваем минимальный размер
      const minSize = 40;
      if (newWidth < minSize) {
        newWidth = minSize;
        newHeight = newWidth / aspect;
      }
      if (newHeight < minSize) {
        newHeight = minSize;
        newWidth = newHeight * aspect;
      }
      
      // Ограничиваем максимальную ширину шириной контейнера редактора
      const editorContainer = containerRef.current?.closest('.ProseMirror');
      const maxContainerWidth = editorContainer ? editorContainer.offsetWidth : window.innerWidth;
      if (newWidth > maxContainerWidth) {
        newWidth = maxContainerWidth;
        newHeight = newWidth / aspect;
      }
      
      const newWidthPx = Math.round(newWidth) + 'px';
      const newHeightPx = Math.round(newHeight) + 'px';
      
      // Сохраняем последние размеры
      lastResizeRef.current = { width: newWidthPx, height: newHeightPx };
      
      // Сохраняем размеры в localStorage
      saveImageSize(newWidthPx, newHeightPx);
      
      // Обновляем атрибуты через updateAttributes
      if (typeof updateAttributes === 'function') {
        updateAttributes({
          width: newWidthPx,
          height: newHeightPx
        });
      }

      // Применяем инлайн-стили для немедленного визуального обновления (как в ResizablePDF, строка 39-40)
      const imgEl = imgRef.current || containerRef.current?.querySelector('img');
        if (imgEl) {
          imgEl.style.width = newWidthPx;
          imgEl.style.height = newHeightPx;
          
          // Обновляем размеры изображения для масштабирования текста
          const newWidth = parseFloat(newWidthPx);
          const newHeight = parseFloat(newHeightPx);
          const oldWidth = imageSizeRef.current.width || newWidth;
          const oldHeight = imageSizeRef.current.height || newHeight;
          
          // Масштабируем текстовые элементы пропорционально изменению размера изображения
          if (oldWidth && oldHeight && (oldWidth !== newWidth || oldHeight !== newHeight)) {
            const scaleX = newWidth / oldWidth;
            const scaleY = newHeight / oldHeight;
            // Используем средний масштаб для сохранения пропорций
            const scale = (scaleX + scaleY) / 2;
            
            setTextElements(prev => prev.map(textEl => {
              // Если есть проценты, используем их для пересчета позиции
              if (textEl.xPercent !== undefined && textEl.yPercent !== undefined) {
                return {
                  ...textEl,
                  x: (textEl.xPercent / 100) * newWidth,
                  y: (textEl.yPercent / 100) * newHeight,
                  fontSize: textEl.baseFontSize ? textEl.baseFontSize * scale : textEl.fontSize * scale,
                  baseImageWidth: newWidth,
                  baseImageHeight: newHeight
                };
              } else {
                // Если процентов нет, пересчитываем из старых координат
                return {
                  ...textEl,
                  x: textEl.x * scaleX,
                  y: textEl.y * scaleY,
                  xPercent: (textEl.x / newWidth) * 100,
                  yPercent: (textEl.y / newHeight) * 100,
                  fontSize: textEl.baseFontSize ? textEl.baseFontSize * scale : textEl.fontSize * scale,
                  baseFontSize: textEl.baseFontSize || textEl.fontSize,
                  baseImageWidth: newWidth,
                  baseImageHeight: newHeight
                };
              }
            }));
            
            // Обновляем сохраненные размеры
            imageSizeRef.current = { width: newWidth, height: newHeight };
        }
      }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      
      // Финальное обновление с последними размерами (через updateAttributes, как у видео)
      if (lastResizeRef.current.width && lastResizeRef.current.height) {
        // Сохраняем в localStorage
        saveImageSize(lastResizeRef.current.width, lastResizeRef.current.height);
        
        // Обновляем атрибуты узла
        if (typeof updateAttributes === 'function') {
        updateAttributes({
          width: lastResizeRef.current.width,
          height: lastResizeRef.current.height
        });
        } else if (editor && typeof getPos === 'function') {
          // Fallback: используем команду редактора напрямую
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.chain().focus().command(({ tr }) => {
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                width: lastResizeRef.current.width,
                height: lastResizeRef.current.height,
              });
              return true;
            }).run();
          }
        }
      }

      // Финальное обновление с последними размерами
      if (lastResizeRef.current.width && lastResizeRef.current.height) {
        // Сохраняем в localStorage
        saveImageSize(lastResizeRef.current.width, lastResizeRef.current.height);
        
        // Обновляем атрибуты узла
        if (typeof updateAttributes === 'function') {
          updateAttributes({
            width: lastResizeRef.current.width,
            height: lastResizeRef.current.height
          });
        }
        
        // Финально применяем инлайн-стили (как в ResizablePDF)
        const imgEl = imgRef.current || containerRef.current?.querySelector('img');
        if (imgEl) {
          imgEl.style.width = lastResizeRef.current.width;
          imgEl.style.height = lastResizeRef.current.height;
        }
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Drag logic (only by handle)
  const handleDragStart = (e) => {
    setDragging(true);
    e.dataTransfer.setData('text/plain', '');
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragEnd = (e) => {
    setDragging(false);
    // Let TipTap/ProseMirror handle the actual move
  };

  // Toolbar actions (мемоизированные)
  // Функция выравнивания удалена
  
  const handleDelete = useCallback(() => deleteNode(), [deleteNode]);

  const handleDraw = useCallback(() => {
    if (isDrawingMode) {
      // Выходим из режима рисования - сохраняем рисунок перед выходом
      saveDrawingToImage();
    } else {
      // Входим в режим рисования
      // НЕ обновляем firstOriginalImageSrcRef при входе в режим рисования
      // Он должен содержать самое первое исходное изображение без рисунков
      // Обновляем originalImageSrcRef на текущее изображение (которое может содержать уже сохраненные рисунки)
      if (imgRef.current) {
        originalImageSrcRef.current = imgRef.current.src;
      }
      // Выходим из режима текста при входе в режим рисования
      setIsTextMode(false);
    }
    setIsDrawingMode(!isDrawingMode);
  }, [isDrawingMode, saveDrawingToImage]);
  
  // Функция для рендеринга текста на изображение через canvas
  // eslint-disable-next-line no-unused-vars
  const renderTextOnImage = useCallback(() => {
    if (!imgRef.current || textElements.length === 0) return;
    
    const img = imgRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.offsetWidth;
    canvas.height = img.naturalHeight || img.offsetHeight;
    const ctx = canvas.getContext('2d');
    
    // Рисуем исходное изображение
    const tempImg = new Image();
    tempImg.crossOrigin = 'anonymous';
    tempImg.onload = () => {
      ctx.drawImage(tempImg, 0, 0, canvas.width, canvas.height);
      
      // Масштабируем координаты текста относительно natural размеров
      const scaleX = canvas.width / img.offsetWidth;
      const scaleY = canvas.height / img.offsetHeight;
      
      // Рисуем тексты
      textElements.forEach(textEl => {
        ctx.font = `${textEl.fontSize * scaleX}px ${textEl.fontFamily}`;
        ctx.fillStyle = textEl.color;
        ctx.fillText(textEl.text, textEl.x * scaleX, textEl.y * scaleY);
      });
      
      // Сохраняем обновленное изображение
      const dataUrl = canvas.toDataURL('image/png');
      safeUpdateAttributes({ src: dataUrl });
      
      // Очищаем текстовые элементы после рендеринга
      setTextElements([]);
    };
    tempImg.src = img.src;
  }, [textElements, safeUpdateAttributes]);
  
  const handleTextMode = useCallback(() => {
    if (isTextMode) {
      // Выходим из режима текста
      setIsTextMode(false);
    } else {
      // Входим в режим текста
      // Выходим из режима рисования при входе в режим текста
      if (isDrawingMode) {
        saveDrawingToImage();
      }
      setIsTextMode(true);
    }
  }, [isTextMode, isDrawingMode, saveDrawingToImage]);
  

  
  // eslint-disable-next-line no-unused-vars
  const handleReplace = useCallback(() => {
    const url = window.prompt('Image URL', node.attrs.src);
    if (url) safeUpdateAttributes({ src: url });
  }, [node.attrs.src, safeUpdateAttributes]);



  // Используем стабильный src, чтобы изображение не пропадало при перерендере
  // Приоритет: peerLiveSrc (peer's in-progress drawing, see useExerciseSync
  // above) > stableSrc > node.attrs.src > originalSrcRef
  const imageSrc = useMemo(() => {
    if (peerLiveSrc) return peerLiveSrc;
    if (stableSrc) return stableSrc;
    if (node.attrs.src) return node.attrs.src;
    if (originalSrcRef.current) return originalSrcRef.current;
    return '';
  }, [peerLiveSrc, stableSrc, node.attrs.src]);
  
  // Упрощенный стиль изображения, как в ResizableVideo - используем атрибуты напрямую
  const imageStyle = {
    margin: '0',
    padding: '0',
      display: 'block',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    objectFit: 'contain',
    cursor: isTextMode && !isDrawingMode ? 'text' : (isDrawingMode ? 'crosshair' : 'default'),
    };

  // Упрощенный стиль контейнера, как в ResizableVideo
  const containerStyle = {
    position: 'relative',
    display: 'inline-block',
    margin: '0',
    padding: '0',
    cursor: isDrawingMode ? 'crosshair' : (dragging ? 'grabbing' : 'default'),
  };

  return (
    <NodeViewWrapper draggable={!isDrawingMode} onDragStart={isDrawingMode ? undefined : handleDragStart} onDragEnd={isDrawingMode ? undefined : handleDragEnd}>
      <div
        ref={containerRef}
        style={containerStyle}
        draggable={!isDrawingMode}
        onDragStart={isDrawingMode ? undefined : handleDragStart}
        onDragEnd={isDrawingMode ? undefined : handleDragEnd}
      >
        {selected && (
          <div
            style={{
              position: 'absolute',
              top: -35,
              left: 0,
              zIndex: 1000,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            {/* Icon toolbar */}
              <div
              style={{
                background: 'white',
                border: '1px solid #eee',
                borderRadius: 6,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                display: 'flex',
                gap: 8,
                padding: 4,
                alignItems: 'center',
                  pointerEvents: 'auto'
              }}
            >
              <IconPencil 
                size={18}
                onClick={handleDraw}
                style={{ cursor: 'pointer', color: isDrawingMode ? '#ff6b35' : '#666666', padding: 2, borderRadius: 3, transition: 'all 0.2s ease', backgroundColor: isDrawingMode ? '#fff3e0' : 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDrawingMode ? '#fff3e0' : '#f0f0f0'; e.currentTarget.style.color = '#ee5a24'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isDrawingMode ? '#fff3e0' : 'transparent'; e.currentTarget.style.color = isDrawingMode ? '#ff6b35' : '#666666'; }}
                title={isDrawingMode ? 'Exit drawing mode' : 'Draw on image'}
              />
              <div
                onClick={(e) => { 
                  e.stopPropagation(); 
                  handleTextMode();
                }}
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                style={{ 
                  cursor: 'pointer', 
                  color: isTextMode ? '#ff6b35' : '#666666', 
                  padding: 2, 
                  borderRadius: 3, 
                  transition: 'all 0.2s ease',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  lineHeight: '18px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  pointerEvents: 'auto',
                  backgroundColor: isTextMode ? '#fff3e0' : 'transparent'
                }}
                onMouseEnter={(e) => { 
                  e.currentTarget.style.backgroundColor = isTextMode ? '#fff3e0' : '#f0f0f0'; 
                  e.currentTarget.style.color = '#ee5a24';
                  e.currentTarget.style.cursor = 'pointer';
                }}
                onMouseLeave={(e) => { 
                  e.currentTarget.style.backgroundColor = isTextMode ? '#fff3e0' : 'transparent'; 
                  e.currentTarget.style.color = isTextMode ? '#ff6b35' : '#666666';
                  e.currentTarget.style.cursor = 'pointer';
                }}
                onMouseMove={(e) => {
                  e.currentTarget.style.cursor = 'pointer';
                }}
                title={isTextMode ? 'Exit text mode' : 'Add text to image'}
              >
                <span style={{ cursor: 'pointer', userSelect: 'none', pointerEvents: 'none' }}>T</span>
              </div>
              {!isStudent && (
                <IconTrash
                  size={18}
                  onClick={handleDelete}
                  style={{ cursor: 'pointer', color: '#666666', padding: 2, borderRadius: 3, transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; e.currentTarget.style.color = '#ee5a24'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#666666'; }}
                  title="Delete image"
                />
              )}
            </div>

            {/* Compact drawing panel, same height, adjacent */}
            {isDrawingMode && (
              <div
                ref={drawingPanelRef}
                style={{
                  background: '#ffffff',
                  border: '1px solid #eee',
                  borderRadius: 6,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 6px',
                  pointerEvents: 'auto'
                }}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); }}
                onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
              >
                {/* Colors */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {['#000000','#ee5a24','#ff0000','#1e3a8a','#10b981','#f59e0b','#ffffff'].map((c) => (
                    <button
                      key={c}
                      onClick={(e) => { e.stopPropagation(); setIsEraser(false); setDrawingColor(c); }}
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: c.toLowerCase() === '#ffffff' ? '1px solid rgba(0,0,0,0.2)' : '1px solid rgba(0,0,0,0.1)',
                        background: c,
                        cursor: 'pointer',
                        boxShadow: drawingColor === c ? '0 0 0 2px rgba(238,90,36,0.6)' : 'none'
                      }}
                      onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    />
                  ))}
                </div>

                {/* Size: three presets */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {[3, 7, 12].map((sz) => (
                    <button
                      key={sz}
                      onClick={(e) => { e.stopPropagation(); setBrushSize(sz); }}
                      title={`Size ${sz}`}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        border: '1px solid rgba(0,0,0,0.12)',
                        background: '#ffffff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: brushSize === sz ? '0 0 0 2px rgba(238,90,36,0.6)' : 'none'
                      }}
                      onMouseDown={(e) => { e.stopPropagation(); }}
                      onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    >
                      <div style={{
                        width: Math.max(4, sz),
                        height: Math.max(4, sz),
                        borderRadius: '50%',
                        background: isEraser ? '#9ca3af' : drawingColor,
                      }} />
                    </button>
                  ))}
                </div>

                {/* Eraser toggle */}
                <button
                  onClick={(e) => { e.stopPropagation(); setIsEraser((v) => !v); }}
                  title={isEraser ? 'Eraser (on)' : 'Eraser (off)'}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 6,
                    border: '1px solid rgba(0,0,0,0.12)',
                    background: isEraser ? '#fee2e2' : '#ffffff',
                    color: '#111827',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e)=>{ e.currentTarget.style.background = isEraser ? '#fecaca' : '#f3f4f6'; }}
                  onMouseLeave={(e)=>{ e.currentTarget.style.background = isEraser ? '#fee2e2' : '#ffffff'; }}
                  onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style={{ display: 'block' }}>
                    <path d="M8.086 2.207a2 2 0 0 1 2.828 0l3.879 3.879a2 2 0 0 1 0 2.828l-5.5 5.5A2 2 0 0 1 7.879 15H5.12a2 2 0 0 1-1.414-.586l-2.5-2.5a2 2 0 0 1 0-2.828zm2.121.707a1 1 0 0 0-1.414 0L4.16 7.547l5.293 5.293 4.633-4.633a1 1 0 0 0 0-1.414zM8.746 13.547 3.453 8.254 1.914 9.793a1 1 0 0 0 0 1.414l2.5 2.5a1 1 0 0 0 .707.293H7.88a1 1 0 0 0 .707-.293z"/>
                  </svg>
                </button>

                {/* Clear */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Очищаем текущий canvas
                    if (canvasRef.current) {
                      const ctx = canvasRef.current.getContext('2d');
                      ctx && ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                    }
                    
                    // Восстанавливаем самое первое исходное изображение без всех рисунков
                    // Используем firstOriginalImageSrcRef, который должен содержать исходное изображение
                    // Если он не установлен или содержит dataUrl (что означает, что он был перезаписан), используем originalSrcRef
                    let originalSrc = firstOriginalImageSrcRef.current;
                    if (!originalSrc || originalSrc.startsWith('data:image/')) {
                      originalSrc = originalSrcRef.current;
                    }
                    // Если originalSrc является blob URL, пытаемся загрузить его и конвертировать в dataUrl
                    if (originalSrc && originalSrc.startsWith('blob:')) {
                      const img = new Image();
                      img.crossOrigin = 'anonymous';
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.naturalWidth;
                        canvas.height = img.naturalHeight;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        const dataUrl = canvas.toDataURL('image/png');
                        // Обновляем refs с dataUrl
                        firstOriginalImageSrcRef.current = dataUrl;
                        originalSrcRef.current = dataUrl;
                        // Восстанавливаем изображение
                        setStableSrc(dataUrl);
                        safeUpdateAttributes({ src: dataUrl });
                        originalImageSrcRef.current = dataUrl;
                        if (imgRef.current) {
                          imgRef.current.src = dataUrl;
                        }
                      };
                      img.onerror = () => {
                        console.error('Failed to load blob URL for clearing');
                      };
                      img.src = originalSrc;
                      return; // Выходим, так как восстановление произойдет в onload
                    }
                    // Если и originalSrcRef содержит dataUrl, пытаемся найти исходное изображение
                    // Это может произойти, если компонент был смонтирован с изображением, которое уже содержало рисунки
                    if (!originalSrc || originalSrc.startsWith('data:image/')) {
                      // Если оба ref содержат dataUrl, значит исходное изображение потеряно
                      // В этом случае просто очищаем canvas и оставляем текущее изображение
                      console.warn('Could not find original image without drawings. Clearing canvas only.');
                      // Очищаем canvas, но не восстанавливаем изображение
                      return;
                    }
                    
                    // Обновляем stableSrc на исходное изображение
                    setStableSrc(originalSrc);
                    // Обновляем атрибуты узла
                    safeUpdateAttributes({ src: originalSrc });
                    // Обновляем originalImageSrcRef для следующего сеанса рисования
                    originalImageSrcRef.current = originalSrc;
                    // Убеждаемся, что firstOriginalImageSrcRef содержит исходное изображение
                    if (firstOriginalImageSrcRef.current !== originalSrc) {
                      firstOriginalImageSrcRef.current = originalSrc;
                    }
                    // Также обновляем originalSrcRef, если он содержит dataUrl
                    if (originalSrcRef.current && originalSrcRef.current.startsWith('data:image/')) {
                      originalSrcRef.current = originalSrc;
                    }
                    // Принудительно обновляем src изображения
                    if (imgRef.current) {
                      imgRef.current.src = originalSrc + (originalSrc.includes('?') ? '&' : '?') + 't=' + Date.now();
                      setTimeout(() => {
                        if (imgRef.current) {
                          imgRef.current.src = originalSrc;
                        }
                      }, 50);
                    }
                  }}
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.12)', background: '#f3f4f6', cursor: 'pointer' }}
                  onMouseEnter={(e)=>{ e.currentTarget.style.background='#e5e7eb'; }}
                  onMouseLeave={(e)=>{ e.currentTarget.style.background='#f3f4f6'; }}
                  onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
        
        <img
          ref={imgRef}
          src={imageSrc}
          alt={node.attrs.alt || ''}
          width={node.attrs.width}
          height={node.attrs.height}
          style={imageStyle}
          onMouseDown={(e) => {
            if (isDrawingMode) {
              e.preventDefault();
            } else if (isTextMode) {
              e.preventDefault();
              e.stopPropagation();
              // Добавляем новый текстовый элемент в позицию клика относительно изображения
              const imgRect = imgRef.current?.getBoundingClientRect();
              if (imgRect) {
                // Вычисляем координаты относительно изображения
                const x = e.clientX - imgRect.left;
                // Учитываем высоту текста, чтобы курсор был в начале текста, а не ниже
                const y = e.clientY - imgRect.top - 12; // 12px - примерная высота текста
                const newTextId = `text_${Date.now()}`;
                // Сохраняем размеры изображения на момент создания текста
                const imgWidth = imgRect.width;
                const imgHeight = imgRect.height;
                
                setTextElements(prev => [...prev, {
                  id: newTextId,
                  x: Math.max(0, x),
                  y: Math.max(0, y),
                  // Сохраняем координаты относительно размера изображения (в процентах)
                  xPercent: (Math.max(0, x) / imgWidth) * 100,
                  yPercent: (Math.max(0, y) / imgHeight) * 100,
                  // Сохраняем размер изображения на момент создания текста
                  baseImageWidth: imgWidth,
                  baseImageHeight: imgHeight,
                  text: '',
                  fontSize: 16,
                  baseFontSize: 16, // Сохраняем базовый размер шрифта
                  color: '#000000',
                  fontFamily: 'Arial',
                  fontWeight: 'normal',
                  fontStyle: 'normal',
                  textDecoration: 'none'
                }]);
                setEditingTextId(newTextId);
              }
            }
          }}
          onDragStart={isDrawingMode || isTextMode ? (e) => e.preventDefault() : undefined}
          onLoad={(e) => {
            // Пропускаем обработку, если мы в режиме текста, чтобы избежать перезагрузки изображения
            if (isTextMode) return;
            
            // Сохраняем aspect ratio из реальных размеров изображения
            const img = e.target;
            if (img.naturalWidth && img.naturalHeight) {
              aspectRatioRef.current = img.naturalWidth / img.naturalHeight;
            }
            
            // Сохраняем текущие размеры изображения для масштабирования текста
            const rect = img.getBoundingClientRect();
            if (rect.width && rect.height) {
              imageSizeRef.current = { width: rect.width, height: rect.height };
            }
            
            // При загрузке изображения применяем сохраненные размеры, если они есть
            const saved = loadImageSize();
            if (saved && saved.width && saved.height && 
                (!node.attrs.width || node.attrs.width === 'auto' || 
                 !node.attrs.height || node.attrs.height === 'auto')) {
              safeUpdateAttributes({ width: saved.width, height: saved.height });
            }
          }}
        />
        
        {/* Текстовые элементы поверх изображения - всегда видимы */}
        {textElements.map((textEl) => (
          <div
            key={textEl.id}
            data-text-id={textEl.id}
            style={{
              position: 'absolute',
              left: `${textEl.x}px`,
              top: `${textEl.y}px`,
              zIndex: editingTextId === textEl.id ? 20 : (draggingTextId === textEl.id ? 15 : 10),
              cursor: editingTextId === textEl.id ? 'text' : (draggingTextId === textEl.id ? 'grabbing' : 'grab'),
              pointerEvents: 'auto',
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (editingTextId === textEl.id) {
                // Если редактируем, не начинаем перетаскивание
                return;
              }
              // Начинаем перетаскивание
              const imgRect = imgRef.current?.getBoundingClientRect();
              if (imgRect) {
                const offsetX = e.clientX - imgRect.left - textEl.x;
                const offsetY = e.clientY - imgRect.top - textEl.y;
                setDragOffset({ x: offsetX, y: offsetY });
                setDraggingTextId(textEl.id);
                setEditingTextId(null);
                // Сохраняем выбранный текстовый элемент для панели редактирования
                const elementId = textEl.id;
                window.selectedImageTextElement = {
                  id: elementId,
                  fontFamily: textEl.fontFamily,
                  fontSize: textEl.fontSize,
                  color: textEl.color,
                  fontWeight: textEl.fontWeight || 'normal',
                  fontStyle: textEl.fontStyle || 'normal',
                  textDecoration: textEl.textDecoration || 'none',
                  updateCallback: (updates) => {
                    // Сразу обновляем стили input поля
                    updateInputStyles(updates, elementId);
                    
                    setTextElements(prev => {
                      const updated = prev.map(t => 
                        t.id === elementId ? { ...t, ...updates } : t
                      );
                      // Обновляем selectedImageTextElement после применения изменений
                      if (window.selectedImageTextElement && window.selectedImageTextElement.id === elementId) {
                        const updatedElement = updated.find(t => t.id === elementId);
                        if (updatedElement) {
                          Object.assign(window.selectedImageTextElement, {
                            fontFamily: updatedElement.fontFamily,
                            fontSize: updatedElement.fontSize,
                            color: updatedElement.color,
                            fontWeight: updatedElement.fontWeight || 'normal',
                            fontStyle: updatedElement.fontStyle || 'normal',
                            textDecoration: updatedElement.textDecoration || 'none'
                          });
                        }
                      }
                      // Триггерим обновление панели
                      if (window.updateToolbar) {
                        setTimeout(() => window.updateToolbar(), 0);
                      }
                      return updated;
                    });
                  }
                };
                // Триггерим обновление панели
                if (window.updateToolbar) {
                  window.updateToolbar();
                }
              }
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setEditingTextId(textEl.id);
              setDraggingTextId(null);
              // Сохраняем выбранный текстовый элемент для панели редактирования
              const elementId = textEl.id;
              window.selectedImageTextElement = {
                id: elementId,
                fontFamily: textEl.fontFamily,
                fontSize: textEl.fontSize,
                color: textEl.color,
                updateCallback: (updates) => {
                  // Сразу обновляем стили input поля
                  updateInputStyles(updates, elementId);
                  
                  setTextElements(prev => {
                    const updated = prev.map(t => 
                      t.id === elementId ? { ...t, ...updates } : t
                    );
                    // Обновляем selectedImageTextElement после применения изменений
                    if (window.selectedImageTextElement && window.selectedImageTextElement.id === elementId) {
                      const updatedElement = updated.find(t => t.id === elementId);
                      if (updatedElement) {
                        Object.assign(window.selectedImageTextElement, {
                          fontFamily: updatedElement.fontFamily,
                          fontSize: updatedElement.fontSize,
                          color: updatedElement.color,
                          fontWeight: updatedElement.fontWeight || 'normal',
                          fontStyle: updatedElement.fontStyle || 'normal',
                          textDecoration: updatedElement.textDecoration || 'none'
                        });
                      }
                    }
                    // Триггерим обновление панели
                    if (window.updateToolbar) {
                      setTimeout(() => window.updateToolbar(), 0);
                    }
                    return updated;
                  });
                }
              };
              // Триггерим обновление панели
              if (window.updateToolbar) {
                window.updateToolbar();
              }
            }}
          >
            {editingTextId === textEl.id ? (
              <input
                type="text"
                value={textEl.text}
                className="image-text-input"
                style={{
                  fontSize: `${textEl.fontSize}px`,
                  color: textEl.color,
                  fontFamily: textEl.fontFamily,
                  fontWeight: textEl.fontWeight || 'normal',
                  fontStyle: textEl.fontStyle || 'normal',
                  textDecoration: textEl.textDecoration || 'none',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  padding: 0,
                  margin: 0,
                  width: 'auto',
                  minWidth: '20px',
                }}
                onChange={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                  setTextElements(prev => prev.map(t => 
                    t.id === textEl.id ? { ...t, text: e.target.value } : t
                  ));
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                  // Блокируем фокус редактора
                  if (editor) {
                    editor.setEditable(false);
                  }
                  // Устанавливаем курсор в конец текста
                  setTimeout(() => {
                    const length = e.target.value.length;
                    e.target.setSelectionRange(length, length);
                  }, 0);
                  // Сбрасываем флаг нового элемента, так как началось редактирование
                  if (newlyCreatedTextIdRef.current === textEl.id) {
                    newlyCreatedTextIdRef.current = null;
                  }
                }}
                onBlur={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                  
                  // Проверяем, не перешел ли фокус на панель редактирования
                  // Используем setTimeout, чтобы проверить активный элемент после того, как фокус перешел
                  setTimeout(() => {
                    const activeElement = document.activeElement;
                    const toolbarElement = document.querySelector('[data-toolbar-element="true"]');
                    const isToolbarFocus = toolbarElement && (
                      toolbarElement.contains(activeElement) || 
                      toolbarElement === activeElement ||
                      activeElement.closest('[data-toolbar-element="true"]')
                    );
                    
                    // Если фокус перешел на панель, не выходим из режима редактирования
                    if (isToolbarFocus) {
                      // Сохраняем выбранный элемент для панели
                      const currentText = e.target.value.trim();
                      if (currentText) {
                        // Обновляем selectedImageTextElement с актуальными данными из состояния
                        const currentTextElement = textElements.find(t => t.id === textEl.id);
                        if (currentTextElement) {
                          window.selectedImageTextElement = {
                            id: currentTextElement.id,
                            fontFamily: currentTextElement.fontFamily,
                            fontSize: currentTextElement.fontSize,
                            color: currentTextElement.color,
                            updateCallback: (updates) => {
                              const elementId = currentTextElement.id;
                              // Сразу обновляем стили input поля
                              updateInputStyles(updates, elementId);
                              
                              setTextElements(prev => {
                                const updated = prev.map(t => 
                                  t.id === elementId ? { ...t, ...updates } : t
                                );
                                // Обновляем selectedImageTextElement после применения изменений
                                if (window.selectedImageTextElement && window.selectedImageTextElement.id === elementId) {
                                  const updatedElement = updated.find(t => t.id === elementId);
                                  if (updatedElement) {
                                    Object.assign(window.selectedImageTextElement, {
                                      fontFamily: updatedElement.fontFamily,
                                      fontSize: updatedElement.fontSize,
                                      color: updatedElement.color
                                    });
                                  }
                                }
                                // Триггерим обновление панели
                                if (window.updateToolbar) {
                                  setTimeout(() => window.updateToolbar(), 0);
                                }
                                return updated;
                              });
                            }
                          };
                        }
                      }
                      // Триггерим обновление панели
                      if (window.updateToolbar) {
                        window.updateToolbar();
                      }
                      // Возвращаем фокус на input поле
                      e.target.focus();
                      return;
                    }
                    
                    // Если фокус не на панели, выходим из режима редактирования
                    const currentText = e.target.value.trim();
                    // Если текст пустой, удаляем элемент
                    if (!currentText) {
                      setTextElements(prev => prev.filter(t => t.id !== textEl.id));
                      // Очищаем выбранный элемент
                      window.selectedImageTextElement = null;
                    } else {
                      // Обновляем выбранный элемент после редактирования
                      const elementId = textEl.id;
                      window.selectedImageTextElement = {
                        id: elementId,
                        fontFamily: textEl.fontFamily,
                        fontSize: textEl.fontSize,
                        color: textEl.color,
                        updateCallback: (updates) => {
                          // Сразу обновляем стили input поля
                          updateInputStyles(updates, elementId);
                          
                          setTextElements(prev => {
                            const updated = prev.map(t => 
                              t.id === elementId ? { ...t, ...updates } : t
                            );
                            // Обновляем selectedImageTextElement после применения изменений
                            if (window.selectedImageTextElement && window.selectedImageTextElement.id === elementId) {
                              const updatedElement = updated.find(t => t.id === elementId);
                              if (updatedElement) {
                                Object.assign(window.selectedImageTextElement, {
                                  fontFamily: updatedElement.fontFamily,
                                  fontSize: updatedElement.fontSize,
                                  color: updatedElement.color,
                                  fontWeight: updatedElement.fontWeight || 'normal',
                                  fontStyle: updatedElement.fontStyle || 'normal',
                                  textDecoration: updatedElement.textDecoration || 'none'
                                });
                              }
                            }
                            // Триггерим обновление панели
                            if (window.updateToolbar) {
                              setTimeout(() => window.updateToolbar(), 0);
                            }
                            return updated;
                          });
                        }
                      };
                    }
                    setEditingTextId(null);
                    // Восстанавливаем редактирование редактора
                    if (editor) {
                      editor.setEditable(true);
                    }
                    // Триггерим обновление панели
                    if (window.updateToolbar) {
                      window.updateToolbar();
                    }
                  }, 100);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  const currentText = e.target.value.trim();
                  // Если текст пустой, удаляем элемент
                  if (!currentText) {
                    setTextElements(prev => prev.filter(t => t.id !== textEl.id));
                    window.selectedImageTextElement = null;
                  } else {
                    // Обновляем выбранный элемент с актуальными данными
                    const currentTextElement = textElements.find(t => t.id === textEl.id);
                    if (currentTextElement) {
                      window.selectedImageTextElement = {
                        id: currentTextElement.id,
                        fontFamily: currentTextElement.fontFamily,
                        fontSize: currentTextElement.fontSize,
                        color: currentTextElement.color,
                          updateCallback: (updates) => {
                            const elementId = currentTextElement.id;
                            setTextElements(prev => {
                              const updated = prev.map(t => 
                                t.id === elementId ? { ...t, ...updates } : t
                              );
                              // Обновляем selectedImageTextElement после применения изменений
                              if (window.selectedImageTextElement && window.selectedImageTextElement.id === elementId) {
                                const updatedElement = updated.find(t => t.id === elementId);
                                if (updatedElement) {
                                  Object.assign(window.selectedImageTextElement, {
                                    fontFamily: updatedElement.fontFamily,
                                    fontSize: updatedElement.fontSize,
                                    color: updatedElement.color
                                  });
                                }
                              }
                              // Триггерим обновление панели
                              if (window.updateToolbar) {
                                setTimeout(() => window.updateToolbar(), 0);
                              }
                              return updated;
                            });
                          }
                      };
                    }
                  }
                  e.target.blur();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  // При Escape удаляем элемент, если он пустой
                  const currentText = e.target.value.trim();
                  if (!currentText) {
                    setTextElements(prev => prev.filter(t => t.id !== textEl.id));
                  }
                  e.target.blur();
                }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                  // Не предотвращаем default, чтобы разрешить выделение текста
                }}
                onKeyUp={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                }}
                onInput={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                }}
                onCompositionStart={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                }}
                onCompositionEnd={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                }}
                onBeforeInput={(e) => {
                  e.stopPropagation();
                  e.nativeEvent?.stopImmediatePropagation();
                }}
                autoFocus
              />
            ) : (
              // Показываем текст только если он не пустой
              textEl.text && textEl.text.trim() ? (
                <div
                  style={{
                    fontSize: `${textEl.fontSize}px`,
                    color: textEl.color,
                    fontFamily: textEl.fontFamily,
                    fontWeight: textEl.fontWeight || 'normal',
                    fontStyle: textEl.fontStyle || 'normal',
                    textDecoration: textEl.textDecoration || 'none',
                    background: draggingTextId === textEl.id ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    border: draggingTextId === textEl.id ? '2px solid #ff6b35' : '1px dashed #ccc',
                    userSelect: 'none',
                    boxShadow: draggingTextId === textEl.id ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                    transition: draggingTextId === textEl.id ? 'none' : 'all 0.2s ease',
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setEditingTextId(textEl.id);
                    setDraggingTextId(null);
                  }}
                >
                  {textEl.text}
                </div>
              ) : null
            )}
          </div>
        ))}
        
        {/* Eraser cursor preview */}
        {isDrawingMode && cursorPos.visible && (
          <div
            style={{
              position: 'absolute',
              left: cursorPos.x - brushSizeRef.current / 2,
              top: cursorPos.y - brushSizeRef.current / 2,
              width: brushSizeRef.current,
              height: brushSizeRef.current,
              borderRadius: '50%',
              border: '1.5px dashed #e11d48',
              background: 'rgba(225,29,72,0.08)',
              pointerEvents: 'none',
              zIndex: 12,
            }}
          />
        )}

        {/* Single bottom-right resize handle (match video behavior) — teacher only */}
        {selected && !isDrawingMode && !isStudent && (
          <div
            className="resize-handle"
            style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-25px',
              width: '18px',
              height: '18px',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.2) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)',
              cursor: 'se-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(0,0,0,0.7)',
              fontSize: '11px',
              fontWeight: 'bold',
              zIndex: 15,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              el.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.4) 100%)';
              el.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              el.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.2) 100%)';
              el.style.transform = 'scale(1)';
            }}
            onMouseDown={(e) => handleResize(e, { name: 'bottom-right' })}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style={{ transform: 'scaleX(-1)', transformOrigin: '50% 50%', pointerEvents: 'none' }}>
              <path fillRule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707m4.344-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707"/>
            </svg>
          </div>
        )}
      </div>
      
      
      
      {/* removed floating portal */}
    </NodeViewWrapper>
  );
};

export default ImageNodeView; 